/**
 * HomeRH — Visão Geral do RH
 *
 * KPIs: OWI, Pulso, Adesão, Setores em Alerta, Custo do Não-Agir
 * Gráficos: Mapa de Calor Organizacional
 * Dados: 100% reais via homeOverview.rh.overview
 */
import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Heart, Activity, ClipboardCheck, DollarSign, ShieldAlert } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { GaugeChart, DonutChart, HeatmapGrid, InsightsCard, IndicatorManualModal } from "@/components/home";
import IndicadorTooltip from "@/components/IndicadorTooltip";

export default function HomeRH() {
  usePageTitle("Visão Geral — RH");

  const { data, isLoading, error } = trpc.homeOverview.rh.overview.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const { data: insightsData } = trpc.dashboardsAdv.insights.byProfile.useQuery(
    { profile: "rh" },
    { refetchOnWindowFocus: false }
  );

  if (isLoading) {
    return (
      <SalviaLayout title="Visão Geral — RH">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <Card key={i} className="p-6"><Skeleton className="h-4 w-24 mb-3" /><Skeleton className="h-[200px]" /></Card>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6"><Skeleton className="h-4 w-24 mb-3" /><Skeleton className="h-10 w-20" /></Card>
            ))}
          </div>
        </div>
      </SalviaLayout>
    );
  }

  if (error) {
    return (
      <SalviaLayout title="Visão Geral — RH">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <h2 className="text-lg font-semibold mb-2">Erro ao carregar dashboard</h2>
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  if (!data) return null;

  const { kpis, heatmap } = data;

  return (
    <SalviaLayout title="Visão Geral — RH">
      <div className="space-y-6">
        {/* ─── 2 Cards Grandes: OWI + Pulso ─── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* OWI — Gauge */}
          <Card className="p-5">
            <div className="flex items-center gap-1 mb-2">
              <h3 className="text-sm font-semibold text-[#002F59]">OWI — Índice de Bem-Estar</h3>
              <IndicadorTooltip indicadorKey="owi" side="right" />
            </div>
            {kpis.owi.hasData ? (
              <>
                <div className="flex items-center justify-center">
                  <GaugeChart
                    value={kpis.owi.score}
                    label="OWI — Índice de Bem-Estar"
                    subtitle={kpis.owi.classificacao}
                    size={200}
                    ranges={[
                      { min: 0, max: 40, color: "#DC2626", label: "Crítico" },
                      { min: 40, max: 60, color: "#D97706", label: "Atenção" },
                      { min: 60, max: 80, color: "#0D7377", label: "Moderado" },
                      { min: 80, max: 100, color: "#16A34A", label: "Saudável" },
                    ]}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Composto: 40% risco invertido + 30% cobertura + 30% distribuição positiva
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <Activity size={40} className="mb-2 opacity-50" />
                <p className="text-sm font-medium">Dados insuficientes para calcular o OWI</p>
                <p className="text-xs mt-1 text-center max-w-[240px]">
                  Conclua avaliações para gerar o índice. O OWI aparecerá aqui assim que houver resultados suficientes.
                </p>
              </div>
            )}
          </Card>

          {/* Pulso da Empresa */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Heart size={18} className="text-[#002F59]" />
              <h3 className="text-sm font-semibold text-[#002F59]">Pulso da Empresa</h3>
              <IndicadorTooltip indicadorKey="pulso" side="top" />
            </div>
            {kpis.pulso.hasData ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">Classificação Geral</span>
                  <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                    kpis.pulso.classificacao.includes("Baixo") ? "bg-green-100 text-green-700" :
                    kpis.pulso.classificacao.includes("Médio") ? "bg-amber-100 text-amber-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    {kpis.pulso.classificacao}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">Score</span>
                  <span className="text-2xl font-bold text-[#002F59]">{kpis.pulso.score}/100</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">Tendência</span>
                  <span className="text-sm font-medium">
                    {String(kpis.pulso.tendencia) === "up" ? "📈 Melhora" :
                     String(kpis.pulso.tendencia) === "down" ? "📉 Piora" : "➡️ Estável"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[180px] text-muted-foreground">
                <Heart size={40} className="mb-2 opacity-50" />
                <p className="text-sm font-medium">Dados insuficientes para calcular o Pulso</p>
                <p className="text-xs mt-1 text-center max-w-[240px]">
                  Conclua avaliações para gerar o diagnóstico de clima psicossocial da empresa.
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* ─── 3 KPIs menores ─── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Taxa de Adesão (Donut) */}
          <Card className="p-5">
            <div className="flex items-center gap-1 mb-3">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Taxa de Adesão</p>
              <IndicadorTooltip indicadorKey="adesao" side="top" size={12} />
            </div>
            <div className="flex items-center justify-center">
              <DonutChart
                percentage={kpis.adesao.percentage}
                completed={kpis.adesao.completed}
                total={kpis.adesao.total}
                label="Taxa de Adesão"
                size={130}
                color="#0D7377"
              />
            </div>
          </Card>

          {/* Setores em Alerta */}
          <Card className="p-5 border-l-4" style={{ borderLeftColor: "#DC2626" }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Setores em Alerta</p>
                  <IndicadorTooltip indicadorKey="setoresAlerta" side="top" size={12} />
                </div>
                <p className="text-3xl font-bold text-[#DC2626] mt-1">{kpis.setoresAlerta.length}</p>
              </div>
              <div className="p-3 rounded-full bg-red-50">
                <ShieldAlert size={24} className="text-[#DC2626]" />
              </div>
            </div>
            {kpis.setoresAlerta.length > 0 ? (
              <div className="space-y-2 mt-3">
                {kpis.setoresAlerta.map((s: any, i: number) => (
                  <div key={i} className="flex items-center justify-between text-xs p-2 bg-muted/50 rounded">
                    <span className="truncate flex-1">{s.sector}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full font-medium ${
                      s.riskLevel === "Alto" ? "bg-red-100 text-red-700" :
                      s.riskLevel === "Médio" ? "bg-amber-100 text-amber-700" :
                      "bg-green-100 text-green-700"
                    }`}>
                      {s.riskLevel} ({s.avgScore})
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mt-2">Nenhum setor em alerta</p>
            )}
          </Card>

          {/* Custo do Não-Agir */}
          <Card className="p-5 border-l-4" style={{ borderLeftColor: "#D97706" }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Custo do Não-Agir</p>
                  <IndicadorTooltip indicadorKey="custoNaoAgir" side="top" size={12} />
                </div>
                <p className="text-2xl font-bold text-[#D97706] mt-1">
                  R$ {kpis.custoNaoAgir.valor.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{kpis.custoNaoAgir.vidasEmRisco} vidas em risco alto</p>
              </div>
              <div className="p-3 rounded-full bg-amber-50">
                <DollarSign size={24} className="text-[#D97706]" />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              {kpis.custoNaoAgir.premissas}
            </p>
          </Card>
        </div>

        {/* ─── Mapa de Calor Organizacional ─── */}
        <Card className="p-6">
          <div className="flex items-center gap-1 mb-4">
            <h3 className="text-sm font-semibold text-foreground">Mapa de Calor Organizacional — Risco por Setor</h3>
            <IndicadorTooltip indicadorKey="mapaCalor" side="top" />
          </div>
          <HeatmapGrid data={heatmap} />
        </Card>

        {/* ─── Insights Operacionais ─── */}
        {insightsData?.insights && insightsData.insights.length > 0 && (
          <Card className="p-6">
            <InsightsCard insights={insightsData.insights as any[]} title="Insights Operacionais" />
          </Card>
        )}

        {/* ─── Empty state ─── */}
        {!kpis.owi.hasData && !kpis.pulso.hasData && kpis.adesao.total === 0 && (
          <Card className="p-8 text-center border-dashed">
            <ClipboardCheck className="mx-auto mb-4 text-muted-foreground opacity-40" size={48} />
            <h2 className="text-lg font-semibold mb-2">Nenhum dado disponível ainda</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Os indicadores de saúde mental da sua empresa aparecerão aqui após a conclusão das avaliações.
              Entre em contato com seu consultor para iniciar o processo.
            </p>
          </Card>
        )}
      </div>
      <IndicatorManualModal profile="rh" />
    </SalviaLayout>
  );
}
